/**
 * KN-only legacy subdomain redirect. No-op for non-KiedyNabory hosts
 * (Hotel B2B, custom tenant domains, localhost). The early `endsWith` guard
 * inside `getLegacyRedirectUrl` returns `null` for anything that is not a
 * `*.kiedynabory.pl` subdomain, so call sites can invoke this unconditionally.
 *
 * @see ../../brand-meta.json — `brands.kiedynabory.hostPatterns`
 */

// NOTE: Domain values intentionally duplicated from domainConfig.ts.
// This module runs before React boot and must be self-contained.
const PRODUCTION_DOMAIN = "kiedynabory.pl";
const TARGET_HOSTNAME = "app.kiedynabory.pl";
const WHITELISTED_SUBDOMAINS = new Set(["app", "api", "docs", "internal", "www"]);

/**
 * Returns a redirect URL if the given hostname is a legacy tenant subdomain,
 * or null if no redirect is needed.
 *
 * Only applies to *.kiedynabory.pl subdomains that are not whitelisted
 * and not on staging (*.staging.kiedynabory.pl).
 *
 * @param hostname - e.g. window.location.hostname
 * @param pathname - e.g. window.location.pathname
 * @param search   - e.g. window.location.search (includes leading "?")
 */
export function getLegacyRedirectUrl(
  hostname: string,
  pathname: string,
  search: string
): string | null {
  // KN-only early return: non-KiedyNabory hosts (Hotel B2B,
  // grupy.hotel-jawor.pl, localhost, etc.) have no legacy redirect to perform.
  if (!hostname.endsWith(`.${PRODUCTION_DOMAIN}`)) {
    return null;
  }

  // Extract subdomain(s) — everything before .kiedynabory.pl
  const subdomain = hostname.slice(0, hostname.length - `.${PRODUCTION_DOMAIN}`.length);

  // No subdomain (apex domain) → no redirect
  if (!subdomain) {
    return null;
  }

  // Staging pattern: subdomain contains ".staging" or is just "staging"
  if (subdomain === "staging" || subdomain.endsWith(".staging") || subdomain.includes(".staging.")) {
    return null;
  }

  // Whitelisted subdomains → no redirect
  if (WHITELISTED_SUBDOMAINS.has(subdomain)) {
    return null;
  }

  // Legacy subdomain → build redirect URL
  return `https://${TARGET_HOSTNAME}${pathname}${search}`;
}
